import { type AxiosError, type AxiosInstance, type AxiosResponse } from 'axios'

import {
  type CustomAxiosRequestConfig,
  type HttpClientInstance
} from '~/domains/platform/infra/http/http-client-factory'

const MAX_LOGGED_PAYLOAD_LENGTH = 5000

function serializePayload(data: unknown) {
  if (data === undefined) return undefined

  const json = JSON.stringify(data)

  return json.length > MAX_LOGGED_PAYLOAD_LENGTH ? `${json.slice(0, MAX_LOGGED_PAYLOAD_LENGTH)}…[truncated]` : json
}

/**
 * Heurística de "resposta vazia": null/undefined, array vazio, objeto sem chaves,
 * ou o wrapper comum `{ data: ... }` sendo, ele mesmo, vazio.
 */
function isEmptyResponseData(data: unknown): boolean {
  if (data === null || data === undefined) return true
  if (Array.isArray(data)) return data.length === 0

  if (typeof data === 'object') {
    const record = data as Record<string, unknown>

    if ('data' in record) return isEmptyResponseData(record.data)

    return Object.keys(record).length === 0
  }

  return false
}

type AcquirerInfraEvents = {
  events: { http: { requestResponseLog: (params: Record<string, unknown>) => void } }
}

let acquirerInfraEvents: AcquirerInfraEvents | undefined

function getAcquirerInfraEvents(): AcquirerInfraEvents {
  if (!acquirerInfraEvents) {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { analiticaInstance } = require('shared/analitica')

    acquirerInfraEvents = analiticaInstance.eventFactory('AcquirerInfra', {
      http: {
        /**
         * @analitica Log do request enviado e do response recebido em uma chamada instrumentada do domínio acquirer
         */
        requestResponseLog: (params: {
          url?: string
          method?: string
          status?: number
          reason?: string
          requestData?: string
          responseData?: string
        }) => ({
          label: 'request.response.log',
          metadata: {
            description: 'Payload enviado e recebido em uma requisição instrumentada do domínio acquirer',
            ...params
          }
        })
      }
    })
  }

  return acquirerInfraEvents as AcquirerInfraEvents
}

/**
 * Loga o payload enviado/recebido. A instrumentação nunca deve quebrar a requisição real,
 * então qualquer falha aqui (ex.: analitica não inicializada em um teste) é apenas ignorada.
 */
function logRequestResponse(params: {
  url?: string
  method?: string
  status?: number
  reason: 'error' | 'empty-response'
  requestData?: unknown
  responseData?: unknown
}) {
  try {
    getAcquirerInfraEvents().events.http.requestResponseLog({
      url: params.url,
      method: params.method,
      status: params.status,
      reason: params.reason,
      requestData: serializePayload(params.requestData),
      responseData: serializePayload(params.responseData)
    })
  } catch {
    // no-op: log de observabilidade nunca deve impactar o fluxo da requisição
  }
}

/**
 * Envolve um client HTTP (retornado por `httpClientFactory`) para logar o payload enviado e
 * recebido quando a chamada falha ou quando a resposta de sucesso vem vazia (ex.: histórico sem
 * itens) — não loga toda chamada saudável, só os casos que valem investigação.
 */
export function withRequestResponseLog(client: HttpClientInstance): HttpClientInstance {
  return async <T>(config?: CustomAxiosRequestConfig) => {
    try {
      const response = await client<T>(config)

      if (isEmptyResponseData(response.data)) {
        logRequestResponse({
          url: config?.url,
          method: config?.method,
          status: response.status,
          reason: 'empty-response',
          requestData: config?.data,
          responseData: response.data
        })
      }

      return response
    } catch (error) {
      const axiosError = error as AxiosError

      logRequestResponse({
        url: config?.url,
        method: config?.method,
        status: axiosError.response?.status,
        reason: 'error',
        requestData: config?.data,
        responseData: axiosError.response?.data
      })

      throw error
    }
  }
}

/**
 * Registra, direto numa instância axios criada localmente (`axios.create`), o log de
 * payload enviado/recebido quando a chamada falha ou quando a resposta de sucesso vem vazia.
 */
export function attachRequestResponseLog(instance: AxiosInstance): AxiosInstance {
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      if (isEmptyResponseData(response.data)) {
        logRequestResponse({
          url: response.config.url,
          method: response.config.method,
          status: response.status,
          reason: 'empty-response',
          requestData: response.config.data,
          responseData: response.data
        })
      }

      return response
    },
    (error: AxiosError) => {
      logRequestResponse({
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        reason: 'error',
        requestData: error.config?.data,
        responseData: error.response?.data
      })

      return Promise.reject(error)
    }
  )

  return instance
}
